import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Volume2, VolumeX, ChevronLeft, ChevronRight, Flag, Play, Pause } from 'lucide-react';
import { fetchStoryplayJson, normalizeStoryScene, splitScenes } from '../../utils/storiesFetcher';
import { getBridge } from '../../utils/storyBridge';
import { buildStorySequence, getNextStory } from '../../utils/storySequencer';
import { getAutoplaysettings } from '../../utils/autoplaysettings';
import useAutoplay from '../../hooks/useAutoplay';
import {
  trackStoryBridgeShown,
  trackStoryBridgeTapped,
  trackStoryBridgeSkipped,
  trackStoryOpen,
  trackStorySceneView,
  trackStoryComplete,
  trackStoryAbandoned,
} from '../../utils/analytics';
import ReportModal from '../../components/feedback/ReportModal';
import QuickFeedback from '../../components/feedback/QuickFeedback';
import StoryTransitionCard from '../../components/stories/StoryTransitionCard';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ITEM_SESSION_KEY = 'explore_stories_selected_item';
const KP_COMPLETED_KEY = 'kp_completed_lessons';

const BRAND_COLORS = {
  pebbles: '#F5A0C0',
  milo: '#3CC9A0',
  vector: '#F5C842',
};
const DEFAULT_BG = '#d4af63';

const HOOK_DELAY_MS = 1500;

// Character emoji shown in the session-complete overlay.
const CHAR_EMOJIS = { pebbles: '🐰', milo: '🦕', vector: '🐱' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function brandColor(characterParam) {
  return BRAND_COLORS[String(characterParam || '').toLowerCase()] || DEFAULT_BG;
}

/** Sample average RGB from an image without touching document.body (ColorThief leaks a visible canvas). */
function sampleImageColor(img) {
  const w = Math.min(img.naturalWidth || img.width || 1, 64);
  const h = Math.min(img.naturalHeight || img.height || 1, 64);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);
  let r = 0;
  let g = 0;
  let b = 0;
  let count = 0;
  for (let i = 0; i < data.length; i += 16) {
    r += data[i];
    g += data[i + 1];
    b += data[i + 2];
    count += 1;
  }
  if (!count) return null;
  return [Math.round(r / count), Math.round(g / count), Math.round(b / count)];
}

function removeStrayColorThiefCanvases() {
  document.querySelectorAll('body > canvas').forEach((node) => node.remove());
}

function markKpLessonComplete(lessonId) {
  if (!lessonId) return;
  try {
    const raw = localStorage.getItem(KP_COMPLETED_KEY);
    const existing = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(existing)) return;
    if (!existing.includes(String(lessonId))) {
      localStorage.setItem(KP_COMPLETED_KEY, JSON.stringify([...existing, String(lessonId)]));
    }
  } catch { /* localStorage unavailable */ }
}

// ---------------------------------------------------------------------------
// ProgressDots
// ---------------------------------------------------------------------------

const MAX_VISIBLE_DOTS = 7;

function ProgressDots({ total, current }) {
  if (total <= 1) return null;

  let indices;
  if (total <= MAX_VISIBLE_DOTS) {
    indices = Array.from({ length: total }, (_, i) => i);
  } else {
    const set = new Set([
      0, 1,
      Math.max(0, current - 1), current, Math.min(total - 1, current + 1),
      total - 2, total - 1,
    ]);
    indices = [...set].sort((a, b) => a - b);
  }

  const dots = [];
  for (let di = 0; di < indices.length; di++) {
    const idx = indices[di];
    const prev = di > 0 ? indices[di - 1] : -1;
    if (di > 0 && idx - prev > 1) {
      dots.push(
        <span key={`gap-${idx}`} style={{ fontSize: 10, opacity: 0.4, color: '#fff', lineHeight: 1 }}>…</span>,
      );
    }
    const isCurrent = idx === current;
    const isVisited = idx < current;
    dots.push(
      <div
        key={idx}
        style={{
          width: isCurrent ? 10 : 7,
          height: isCurrent ? 10 : 7,
          borderRadius: '50%',
          background: isCurrent ? '#fff' : isVisited ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.28)',
          transition: 'all 0.2s ease',
          flexShrink: 0,
        }}
        aria-hidden
      />,
    );
  }

  return (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '4px 0' }}
      aria-label={`Page ${current + 1} of ${total}`}
      role="status"
    >
      {dots}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HookScreen
// ---------------------------------------------------------------------------

function HookScreen({ bridge, storyId, onNavigate, onSkip }) {
  return (
    <motion.div
      key="hook-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0, 0, 0, 0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px 20px', boxSizing: 'border-box',
      }}
      aria-modal="true"
      role="dialog"
      aria-labelledby="hook-title"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        style={{
          background: 'var(--color-bg-card, #fff)',
          borderRadius: 22,
          padding: '28px 24px 24px',
          maxWidth: 380, width: '100%',
          boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
          textAlign: 'center',
        }}
      >
        <div style={{
          fontSize: 11, fontWeight: 800, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: 'var(--color-primary)',
          marginBottom: 10, opacity: 0.9,
        }}>
          {bridge.character}
        </div>
        <p
          id="hook-title"
          style={{
            fontSize: 'clamp(1.05rem, 3.5vw, 1.2rem)', fontWeight: 700,
            color: 'var(--color-text)', lineHeight: 1.45, margin: '0 0 24px',
          }}
        >
          {bridge.hook_text}
        </p>
        <button
          type="button"
          onClick={onNavigate}
          style={{
            width: '100%', padding: '14px 20px', borderRadius: 14, border: 'none',
            background: 'var(--color-primary)', color: '#fff',
            fontSize: 16, fontWeight: 800, cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)', marginBottom: 12,
          }}
        >
          Find out in Explore →
        </button>
        <button
          type="button"
          onClick={onSkip}
          style={{
            background: 'none', border: 'none', fontSize: 14, fontWeight: 600,
            color: 'var(--color-text)', opacity: 0.55, cursor: 'pointer', padding: '8px 12px',
          }}
        >
          Skip for now
        </button>
      </motion.div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// UnlockCelebration
// ---------------------------------------------------------------------------

function UnlockCelebration({ message = '⭐ Next chapter unlocked!' }) {
  return (
    <motion.div
      key="unlock-banner"
      initial={{ opacity: 0, y: -24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'fixed', top: 16, left: '50%',
        transform: 'translateX(-50%)', zIndex: 300,
        background: 'var(--color-primary)', color: '#fff',
        padding: '10px 20px', borderRadius: 40,
        fontSize: 14, fontWeight: 700,
        boxShadow: '0 4px 18px rgba(0,0,0,0.25)',
        whiteSpace: 'nowrap', pointerEvents: 'none',
      }}
      role="status"
      aria-live="polite"
    >
      {message}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// SessionCompleteScreen
// ---------------------------------------------------------------------------

function SessionCompleteScreen({ characterParam, storiesWatched, onKeepWatching, onAllDone }) {
  const emoji = CHAR_EMOJIS[String(characterParam || '').toLowerCase()] || '📖';
  return (
    <motion.div
      key="session-complete"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 250,
        background: 'rgba(0,0,0,0.78)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px 20px', boxSizing: 'border-box',
      }}
      aria-modal="true"
      role="dialog"
      aria-labelledby="session-complete-title"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        style={{
          background: 'var(--color-bg-card, #fff)',
          borderRadius: 24, padding: '36px 28px 28px',
          maxWidth: 360, width: '100%',
          boxShadow: '0 28px 64px rgba(0,0,0,0.45)',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 56, marginBottom: 16, lineHeight: 1 }}>{emoji}</div>
        <h2
          id="session-complete-title"
          style={{
            margin: '0 0 10px', fontSize: 'clamp(1.2rem, 4vw, 1.4rem)',
            fontWeight: 800, color: 'var(--color-text)',
          }}
        >
          Great listening session!
        </h2>
        <p style={{
          margin: '0 0 28px', fontSize: 15, lineHeight: 1.5,
          color: 'var(--color-text)', opacity: 0.7,
        }}>
          You watched {storiesWatched} {storiesWatched === 1 ? 'story' : 'stories'} today.
        </p>
        <button
          type="button"
          onClick={onKeepWatching}
          style={{
            width: '100%', padding: '14px 20px', borderRadius: 14,
            border: 'none', background: 'var(--color-primary)', color: '#fff',
            fontSize: 15, fontWeight: 800, cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0,0,0,0.18)', marginBottom: 12,
          }}
        >
          Keep watching
        </button>
        <button
          type="button"
          onClick={onAllDone}
          style={{
            background: 'none', border: 'none', fontSize: 14, fontWeight: 600,
            color: 'var(--color-text)', opacity: 0.55, cursor: 'pointer', padding: '8px 12px',
          }}
        >
          All done
        </button>
      </motion.div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// StoryPlayerPage
// ---------------------------------------------------------------------------

export default function StoryPlayerPage() {
  const { character: characterParam, storyId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── State ──────────────────────────────────────────────────────────────────
  const [pages, setPages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [storyTitle, setStoryTitle] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [bgColor, setBgColor] = useState(() => brandColor(characterParam));
  const [showHookScreen, setShowHookScreen] = useState(false);
  const [hookDismissed, setHookDismissed] = useState(false);
  const [showUnlockCelebration, setShowUnlockCelebration] = useState(false);
  const [unlockCelebrationMessage, setUnlockCelebrationMessage] = useState('⭐ Next chapter unlocked!');
  const [showSessionComplete, setShowSessionComplete] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [nextStoryData, setNextStoryData] = useState(null);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const audioRef = useRef(null);
  const hookTimerRef = useRef(null);   // existing: fires hook screen on last page (non-autoplay)
  const hookTimeoutRef = useRef(null); // autoplay: auto-skip exercise screen
  const bridgeFiredRef = useRef(false);
  // Stable ref so goTo (defined before useAutoplay) can always call the latest pauseAutoplay.
  const pauseAutoplayRef = useRef(() => {});
  // Track the last image URL passed to extractColor so we skip redundant re-extractions
  // when multiple text pages share the same scene image.
  const lastExtractedImgRef = useRef('');
  // Refs for GA tracking — state is stale in unmount cleanup; refs always reflect latest values.
  const currentIndexRef = useRef(0);
  const storyIdRef = useRef(storyId);
  const characterRef = useRef(characterParam);
  const storyTitleRef = useRef('');
  const pagesRef = useRef([]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const bridge = useMemo(() => getBridge(storyId), [storyId]);
  const backPath = `/stories/${characterParam}`;
  const currentPage = pages[currentIndex] ?? null;

  // ── Audio playback ─────────────────────────────────────────────────────────

  const playAudio = useCallback(async (src) => {
    const el = audioRef.current;
    if (!el) return;
    el.pause();
    if (!src) {
      el.removeAttribute('src');
      try { el.load(); } catch { /* ignore */ }
      return;
    }
    el.src = src;
    el.load();
    await new Promise((resolve) => {
      if (el.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) { resolve(); return; }
      const finish = () => {
        clearTimeout(timeoutId);
        el.removeEventListener('canplay', onCanPlay);
        el.removeEventListener('error', onError);
        resolve();
      };
      const onCanPlay = () => finish();
      const onError = () => finish();
      const timeoutId = setTimeout(finish, 12000);
      el.addEventListener('canplay', onCanPlay, { once: true });
      el.addEventListener('error', onError, { once: true });
    });
    try { await el.play(); } catch { /* autoplay blocked */ }
  }, []);

  // ── Background color extraction ────────────────────────────────────────────

  const extractColor = useCallback(async (imgSrc) => {
    if (!imgSrc) return;
    try {
      const response = await fetch(imgSrc, { mode: 'cors' });
      const blob = await response.blob();
      const objectURL = URL.createObjectURL(blob);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = objectURL;
      img.onload = () => {
        try {
          const rgb = sampleImageColor(img);
          if (rgb) setBgColor(`rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`);
        } catch { /* cross-origin taint — keep current color */ }
        URL.revokeObjectURL(objectURL);
      };
      img.onerror = () => URL.revokeObjectURL(objectURL);
    } catch { /* fetch failed — keep current color */ }
  }, []);

  // ── handleAutoNextStory ─────────────────────────────────────────────────────
  // Navigate to the next story in the cross-character sequence. Called by
  // handleStoryComplete (no bridge) or after the hook screen is dismissed.

  const handleAutoNextStory = useCallback(async (options = {}) => {
    const { force = false, skipPause = false } = options;
    const settings = getAutoplaysettings();
    if (!force && !settings.autoNext) return;

    const sequence = await buildStorySequence();
    const next = getNextStory(storyId, sequence);
    if (!next) return;

    if (skipPause) {
      // Returning from lesson exercise — skip transition card and navigate directly.
      try {
        sessionStorage.setItem(
          ITEM_SESSION_KEY,
          JSON.stringify({ id: next.storyId, title: next.title, url: next.url, image: next.image }),
        );
      } catch { /* sessionStorage unavailable */ }
      navigate(`/stories/${next.characterLower}/${next.storyId}`, { replace: false });
      return;
    }

    // Show transition card — it owns the delay and calls back when ready.
    setNextStoryData(next);
    setShowTransition(true);
  }, [storyId, navigate]);

  // ── Transition card callbacks ──────────────────────────────────────────────

  const handleTransitionContinue = useCallback(() => {
    if (!nextStoryData) return;
    setShowTransition(false);
    try {
      sessionStorage.setItem(
        ITEM_SESSION_KEY,
        JSON.stringify({
          id: nextStoryData.storyId,
          title: nextStoryData.title,
          url: nextStoryData.url,
          image: nextStoryData.image,
        }),
      );
    } catch { /* sessionStorage unavailable */ }
    navigate(`/stories/${nextStoryData.characterLower}/${nextStoryData.storyId}`, { replace: false });
  }, [nextStoryData, navigate]);

  const handleTransitionChoose = useCallback(() => {
    if (!nextStoryData) return;
    setShowTransition(false);
    navigate(`/stories/${nextStoryData.characterLower}`);
  }, [nextStoryData, navigate]);

  // ── handleStoryComplete ────────────────────────────────────────────────────
  // Called by the autoplay hook's onStoryComplete. Decides whether to show the
  // bridge/hook screen (and optionally auto-skip it) or navigate directly to
  // the next story.

  const handleStoryComplete = useCallback(() => {
    trackStoryComplete({
      storyId,
      character: characterRef.current,
      storyTitle: storyTitleRef.current,
      totalScenes: pagesRef.current.length,
    });
    const settings = getAutoplaysettings();
    clearTimeout(hookTimeoutRef.current);

    if (bridge && !bridgeFiredRef.current) {
      bridgeFiredRef.current = true;
      setShowHookScreen(true);
      trackStoryBridgeShown({ storyId, character: bridge.character, lessonId: bridge.lesson_id });

      if (!settings.requireExercise) {
        // Auto-skip the hook screen after the configured timeout, then start
        // the next story. hookTimeoutRef lets handleHookNavigate cancel this.
        hookTimeoutRef.current = setTimeout(() => {
          setShowHookScreen(false);
          handleAutoNextStory();
        }, settings.exerciseTimeoutSeconds * 1000);
      }
      // If requireExercise is true the child must tap "Skip for now" or
      // "Find out in Explore" — no timer is set.
    } else {
      // No bridge (or already shown) — go straight to the next story.
      handleAutoNextStory();
    }
  }, [storyId, bridge, handleAutoNextStory]);

  // ── onSkipExercise ─────────────────────────────────────────────────────────
  // Passed to useAutoplay for API compatibility. Also used as the exercise
  // timeout callback via hookTimeoutRef when needed externally.

  const onSkipExercise = useCallback(() => {
    setShowHookScreen(false);
    handleAutoNextStory();
  }, [handleAutoNextStory]);

  // ── useAutoplay ─────────────────────────────────────────────────────────────

  const { isAutoplaying, pauseAutoplay, resumeAutoplay, stopAutoplay, sessionStoriesPlayed } =
    useAutoplay({
      currentIndex,
      totalScenes: pages.length,
      currentAudioUrl: currentPage?.audio?.[0] ?? null,
      currentTextLength: currentPage?.text?.[0]?.length ?? 0,
      storyId,
      character: characterParam,
      audioRef,
      onNextScene: () => {
        setCurrentIndex((i) => (i < pages.length - 1 ? i + 1 : i));
      },
      onStoryComplete: handleStoryComplete,
      onSkipExercise,
      onSessionStop: () => {
        setShowSessionComplete(true);
      },
    });

  // Keep the stable ref current so goTo can always call the latest pauseAutoplay.
  pauseAutoplayRef.current = pauseAutoplay;

  // ── handleAutoplayToggle ───────────────────────────────────────────────────
  // Unified handler for both the top-bar dot and the nav-row button.
  // Pause: also pauses the audio element so it doesn't end silently while
  //   autoplay is "paused", which would lose the 'ended' event in audio mode.
  // Resume: replays the current scene's audio immediately so the user gets
  //   instant audio feedback that autoplay restarted (avoids the appearance
  //   of nothing happening during the timer countdown).

  const handleAutoplayToggle = useCallback(() => {
    if (isAutoplaying) {
      audioRef.current?.pause();
      pauseAutoplay();
    } else {
      const src = currentPage?.audio?.[0] ?? '';
      if (!isMuted && src) playAudio(src);
      resumeAutoplay();
    }
  }, [isAutoplaying, pauseAutoplay, resumeAutoplay, currentPage, isMuted, playAudio]);

  // ── Load story ─────────────────────────────────────────────────────────────

  const loadStory = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setCurrentIndex(0);
    setPages([]);
    setShowHookScreen(false);
    setHookDismissed(false);
    setShowSessionComplete(false);
    setShowTransition(false);
    setNextStoryData(null);
    bridgeFiredRef.current = false;
    clearTimeout(hookTimeoutRef.current);

    try {
      const raw = sessionStorage.getItem(ITEM_SESSION_KEY);
      const item = raw ? JSON.parse(raw) : null;
      if (!item?.url) {
        setLoadError('No story selected. Please go back and choose a story.');
        return;
      }
      const json = await fetchStoryplayJson(item.url);
      const rawScenes = Array.isArray(json?.scenes) ? json.scenes : [];
      if (!rawScenes.length) {
        setLoadError('This story has no scenes.');
        return;
      }
      const resolvedStoryTitle = json.title || item.title || 'Story';
      setStoryTitle(resolvedStoryTitle);
      storyTitleRef.current = resolvedStoryTitle;
      const normalized = rawScenes.map(normalizeStoryScene);
      const flat = splitScenes(normalized);
      setPages(flat);
      trackStoryOpen({
        storyId: storyIdRef.current,
        character: characterRef.current,
        storyTitle: resolvedStoryTitle,
        totalScenes: flat.length,
      });
    } catch (err) {
      console.error('[StoryPlayerPage] load failed:', err);
      setLoadError('Could not load this story. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Navigation ─────────────────────────────────────────────────────────────
  // Pause autoplay on manual navigation so it doesn't fight the user's choice.
  // The hook auto-resumes after 10 s.

  const goTo = useCallback(
    (index) => {
      if (index < 0 || index >= pages.length) return;
      pauseAutoplayRef.current();
      setCurrentIndex(index);
    },
    [pages.length],
  );

  // ── Hook screen handlers ───────────────────────────────────────────────────

  const handleHookNavigate = useCallback(() => {
    if (!bridge) return;
    // Cancel the auto-skip timer so we don't navigate twice.
    clearTimeout(hookTimeoutRef.current);
    trackStoryBridgeTapped({ storyId, character: bridge.character, lessonId: bridge.lesson_id });
    const dest =
      `${bridge.lesson_path}?from=story` +
      `&character=${encodeURIComponent(bridge.character)}` +
      `&storyId=${encodeURIComponent(storyId)}`;
    navigate(dest);
  }, [bridge, storyId, navigate]);

  const handleHookSkip = useCallback(() => {
    if (bridge) {
      trackStoryBridgeSkipped({ storyId, character: bridge.character, lessonId: bridge.lesson_id });
    }
    clearTimeout(hookTimeoutRef.current);
    setShowHookScreen(false);
    setHookDismissed(true);
    // If autoplay is on, continue to the next story after the user skips.
    const settings = getAutoplaysettings();
    if (settings.enabled && settings.autoNext) {
      handleAutoNextStory();
    }
  }, [bridge, storyId, handleAutoNextStory]);

  // ── Effects ────────────────────────────────────────────────────────────────

  // Reload story when storyId/character changes — unless returning from a lesson
  // exercise (advance to next story instead of replaying the current one).
  useEffect(() => {
    const returned = searchParams.get('returned') === 'true';
    const lessonId = searchParams.get('lessonId');

    if (returned) {
      if (lessonId) markKpLessonComplete(lessonId);
      setLoading(false);
      setUnlockCelebrationMessage('⭐ Great job! Starting next story...');
      setShowUnlockCelebration(true);
      const t = setTimeout(() => {
        setShowUnlockCelebration(false);
        handleAutoNextStory({ force: true, skipPause: true });
      }, 2000);
      return () => clearTimeout(t);
    }

    loadStory();
  }, [loadStory, storyId, characterParam, searchParams, handleAutoNextStory]);

  // ColorThief (legacy) appends full-size canvases to <body>; remove any leftovers.
  useEffect(() => {
    removeStrayColorThiefCanvases();
    return removeStrayColorThiefCanvases;
  }, [currentIndex]);

  // Play audio + extract color on page change
  useEffect(() => {
    if (!currentPage) return;
    trackStorySceneView({
      storyId: storyIdRef.current,
      character: characterRef.current,
      storyTitle: storyTitleRef.current,
      sceneIndex: currentIndex,
      totalScenes: pages.length, // closure value — always current for this render
    });
    const audioSrc = currentPage.audio?.[0] ?? '';
    if (!isMuted) playAudio(audioSrc);
    // Only re-extract color when the image actually changes (skips same-scene pages).
    const imgSrc = currentPage.image?.src ?? '';
    if (imgSrc && imgSrc !== lastExtractedImgRef.current) {
      lastExtractedImgRef.current = imgSrc;
      extractColor(imgSrc);
    }
  }, [currentIndex, currentPage, isMuted, playAudio, extractColor]);

  // Pause audio when muted
  useEffect(() => {
    if (isMuted) audioRef.current?.pause();
  }, [isMuted]);

  // Hook screen timer: only fires when autoplay is OFF. When autoplay is on,
  // handleStoryComplete takes over hook-screen responsibility.
  useEffect(() => {
    clearTimeout(hookTimerRef.current);
    if (
      !isAutoplaying &&
      pages.length > 0 &&
      currentIndex === pages.length - 1 &&
      bridge &&
      !hookDismissed &&
      !bridgeFiredRef.current
    ) {
      hookTimerRef.current = setTimeout(() => {
        bridgeFiredRef.current = true;
        setShowHookScreen(true);
        trackStoryBridgeShown({ storyId, character: bridge.character, lessonId: bridge.lesson_id });
      }, HOOK_DELAY_MS);
    }
    return () => clearTimeout(hookTimerRef.current);
  }, [currentIndex, pages.length, bridge, hookDismissed, storyId, isAutoplaying]);


  // Keep GA tracking refs current so the unmount cleanup always reads latest values.
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { pagesRef.current = pages; }, [pages]);
  useEffect(() => { storyIdRef.current = storyId; }, [storyId]);
  useEffect(() => { characterRef.current = characterParam; }, [characterParam]);

  // Clean up audio and hookTimeout on unmount; fire story_abandoned if not at last scene.
  useEffect(() => {
    return () => {
      if (pagesRef.current.length > 0 && currentIndexRef.current < pagesRef.current.length - 1) {
        trackStoryAbandoned({
          storyId: storyIdRef.current,
          character: characterRef.current,
          storyTitle: storyTitleRef.current,
          lastSceneIndex: currentIndexRef.current,
          totalScenes: pagesRef.current.length,
        });
      }
      const el = audioRef.current;
      if (el) {
        el.pause();
        el.removeAttribute('src');
        try { el.load(); } catch { /* ignore */ }
      }
      clearTimeout(hookTimeoutRef.current);
    };
  }, []);

  // ── Render helpers ─────────────────────────────────────────────────────────

  const totalPages = pages.length;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === totalPages - 1;
  const autoplayEnabled = getAutoplaysettings().enabled;

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', background: brandColor(characterParam),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.8s ease',
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          border: '3px solid rgba(255,255,255,0.3)',
          borderTopColor: '#fff',
          animation: 'story-spin 0.8s linear infinite',
        }} aria-label="Loading story" role="status" />
        <style>{`@keyframes story-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────

  if (loadError) {
    return (
      <div style={{
        minHeight: '100vh', background: brandColor(characterParam),
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        padding: '24px 20px', textAlign: 'center', gap: 20,
      }}>
        <p style={{ color: '#fff', fontSize: 16, lineHeight: 1.5, maxWidth: 340, margin: 0 }}>
          {loadError}
        </p>
        <button
          type="button"
          onClick={loadStory}
          style={{
            padding: '12px 24px', borderRadius: 12,
            border: '2px solid rgba(255,255,255,0.7)',
            background: 'rgba(255,255,255,0.15)', color: '#fff',
            fontSize: 15, fontWeight: 700, cursor: 'pointer',
          }}
        >
          Try again
        </button>
        <button
          type="button"
          onClick={() => navigate(backPath)}
          style={{
            background: 'none', border: 'none',
            color: 'rgba(255,255,255,0.7)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}
        >
          ← Back
        </button>
      </div>
    );
  }

  // ── Main reader ────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        @keyframes story-spin { to { transform: rotate(360deg); } }

        .story-player-outer {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .story-image-panel {
          width: 100%;
          height: 55vh;
          flex-shrink: 0;
          position: relative;
          overflow: hidden;
        }

        .story-image-stage {
          position: absolute;
          inset: 0;
          overflow: hidden;
        }

        .story-image-panel img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
          display: block;
        }

        .story-text-panel {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 20px 24px calc(20px + env(safe-area-inset-bottom, 0px));
          overflow: hidden;
        }

        .story-text {
          margin: 0;
          font-size: clamp(1.05rem, 3.5vw, 1.25rem);
          max-width: 540px;
        }

      `}</style>

      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 40,
          background: bgColor,
          transition: 'background 0.8s ease',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* ── Top bar ── */}
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px 8px', gap: 12, flexShrink: 0,
          }}
        >
          {/* Close */}
          <button
            type="button"
            onClick={() => navigate(backPath)}
            aria-label="Close story"
            style={{
              width: 40, height: 40, borderRadius: '50%', border: 'none',
              background: 'rgba(255,255,255,0.22)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            <X size={20} strokeWidth={2.4} aria-hidden />
          </button>

          {/* Title */}
          <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
            <div style={{
              fontSize: 14, fontWeight: 700, color: '#fff', opacity: 0.9,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {storyTitle}
            </div>
          </div>

          {/* Right cluster: counter + autoplay indicator + report */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>
              {totalPages > 0 ? `${currentIndex + 1}/${totalPages}` : ''}
            </div>

            {/* Autoplay status dot — only visible when the setting is on */}
            {autoplayEnabled && (
              <button
                type="button"
                onClick={handleAutoplayToggle}
                aria-label={isAutoplaying ? 'Pause autoplay' : 'Resume autoplay'}
                title={isAutoplaying ? 'Autoplay on — tap to pause' : 'Autoplay paused — tap to resume'}
                style={{
                  background: 'transparent', border: 'none',
                  cursor: 'pointer', padding: '6px',
                  color: 'rgba(255,255,255,0.6)',
                  display: 'flex', alignItems: 'center',
                  opacity: isAutoplaying ? 1 : 0.45,
                }}
              >
                {/* Show what the button will DO: Pause when playing, Play when paused */}
                {isAutoplaying
                  ? <Pause size={13} strokeWidth={2.5} aria-hidden />
                  : <Play size={13} strokeWidth={2.5} aria-hidden />}
              </button>
            )}

            <button
              type="button"
              onClick={() => setReportOpen(true)}
              aria-label="Report an issue"
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: '8px', color: 'rgba(255,255,255,0.7)',
                display: 'flex', alignItems: 'center',
              }}
            >
              <Flag size={18} aria-hidden />
            </button>
          </div>
        </div>

        {/* ── Progress dots ── */}
        <div style={{ padding: '0 16px 10px', flexShrink: 0 }}>
          <ProgressDots total={totalPages} current={currentIndex} />
        </div>

        {/* ── Content area ── */}
        <div className="story-player-outer">
          {/* Scene image panel */}
          <div className="story-image-panel" style={{ backgroundColor: bgColor }}>
            <div className="story-image-stage" aria-hidden>
              <AnimatePresence mode="wait" initial={false}>
                {currentPage?.image?.src ? (
                  <motion.img
                    key={`img-${currentPage.sceneIndex}`}
                    src={currentPage.image.src}
                    alt=""
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    onError={() => {}}
                  />
                ) : (
                  <motion.div
                    key={`img-placeholder-${currentPage?.sceneIndex}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{
                      position: 'absolute', inset: 0,
                      background: 'rgba(0,0,0,0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 52,
                    }}
                  >
                    📖
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Text panel */}
          <div className="story-text-panel">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={`text-${currentIndex}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.25 }}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', textAlign: 'center',
                  padding: '8px 4px 16px',
                }}
              >
                <p className="story-text" style={{
                  fontStyle: 'italic', fontWeight: 500, color: '#fff',
                  lineHeight: 1.55, textShadow: '0 1px 6px rgba(0,0,0,0.3)',
                }}>
                  {currentPage?.text?.[0] ?? ''}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* QuickFeedback on last scene */}
            {isLast && (
              <QuickFeedback
                type="story"
                contentId={storyId}
                character={characterParam}
                worldId={null}
                totalCount={pages.length}
                completedFully={true}
              />
            )}

            {/* Nav row */}
            <div style={{
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', gap: 12, paddingTop: 4,
            }}>
              <button
                type="button"
                onClick={() => goTo(currentIndex - 1)}
                disabled={isFirst}
                aria-label="Previous page"
                style={{
                  width: 52, height: 52, borderRadius: '50%', border: 'none',
                  background: isFirst ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.22)',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: isFirst ? 'not-allowed' : 'pointer',
                  opacity: isFirst ? 0.4 : 1, flexShrink: 0, transition: 'opacity 0.18s',
                }}
              >
                <ChevronLeft size={26} strokeWidth={2.2} aria-hidden />
              </button>

              {/* Autoplay pause/play — shown in nav row when autoplay setting is on */}
              {autoplayEnabled && (
                <button
                  type="button"
                  onClick={handleAutoplayToggle}
                  aria-label={isAutoplaying ? 'Pause autoplay' : 'Resume autoplay'}
                  style={{
                    width: 44, height: 44, borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.5)',
                    background: isAutoplaying ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.12)',
                    color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', flexShrink: 0,
                    transition: 'background 0.2s',
                  }}
                >
                  {isAutoplaying
                    ? <Pause size={20} strokeWidth={2} aria-hidden />
                    : <Play size={20} strokeWidth={2} aria-hidden />}
                </button>
              )}

              <button
                type="button"
                onClick={() => setIsMuted((m) => !m)}
                aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
                style={{
                  width: 44, height: 44, borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.5)',
                  background: 'rgba(255,255,255,0.12)', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', flexShrink: 0,
                }}
              >
                {isMuted
                  ? <VolumeX size={20} strokeWidth={2} aria-hidden />
                  : <Volume2 size={20} strokeWidth={2} aria-hidden />}
              </button>

              <button
                type="button"
                onClick={() => goTo(currentIndex + 1)}
                disabled={isLast}
                aria-label="Next page"
                style={{
                  width: 52, height: 52, borderRadius: '50%', border: 'none',
                  background: isLast ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.22)',
                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: isLast ? 'not-allowed' : 'pointer',
                  opacity: isLast ? 0.4 : 1, flexShrink: 0, transition: 'opacity 0.18s',
                }}
              >
                <ChevronRight size={26} strokeWidth={2.2} aria-hidden />
              </button>
            </div>
          </div>
        </div>

        {/* Hidden audio element */}
        <audio
          ref={audioRef}
          preload="auto"
          playsInline
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
          aria-hidden
        />

        {/* ── Overlays ── */}
        <AnimatePresence>
          {showUnlockCelebration && (
            <UnlockCelebration key="unlock" message={unlockCelebrationMessage} />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showHookScreen && bridge && (
            <HookScreen
              key="hook"
              bridge={bridge}
              storyId={storyId}
              onNavigate={handleHookNavigate}
              onSkip={handleHookSkip}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showSessionComplete && (
            <SessionCompleteScreen
              key="session-complete"
              characterParam={characterParam}
              storiesWatched={sessionStoriesPlayed}
              onKeepWatching={() => {
                setShowSessionComplete(false);
                handleAutoNextStory();
              }}
              onAllDone={() => navigate('/stories')}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showTransition && nextStoryData && (
            <StoryTransitionCard
              key="story-transition"
              completedStory={{ title: storyTitle, character: characterParam }}
              nextStory={{
                title: nextStoryData.title,
                character: nextStoryData.character,
                image: nextStoryData.image,
              }}
              delaySeconds={getAutoplaysettings().pauseBetweenSeconds}
              onContinue={handleTransitionContinue}
              onChoose={handleTransitionChoose}
            />
          )}
        </AnimatePresence>

        <ReportModal
          isOpen={reportOpen}
          onClose={() => setReportOpen(false)}
          contentType="story"
          contentId={storyId}
          contentTitle={storyTitle}
          character={characterParam}
          worldId={null}
          currentPosition={`scene ${currentIndex + 1} of ${pages.length}`}
        />
      </div>
    </>
  );
}
